import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, User, MapPin, LogOut, Shield, Calendar, Briefcase, CalendarOff, Clock, Gift, Building2, Users, Crown, Camera, AlertCircle, Hash, Lock, Eye, EyeOff, Bell, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { authAPI, holidayAPI, pushAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { compressImage } from '@/lib/imageUtils';
import BottomNav from '@/components/BottomNav';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff < 0) return null;
  const years = Math.floor(diff / 365);
  const months = Math.floor((diff % 365) / 30);
  const days = diff % 30;
  if (years > 0) return `${years}y ${months}m`;
  if (months > 0) return `${months}m ${days}d`;
  return `${days} days`;
}

export default function EmployeeProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [holidays, setHolidays] = useState([]);
  const [leaveStats, setLeaveStats] = useState({ approved: 0, pending: 0 });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    authAPI.getProfile().then(res => {
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    }).catch(() => {});

    holidayAPI.getPublic().then(res => {
      setHolidays(res.data);
    }).catch(() => {});

    api.get('/leaves/my-leaves').then(res => {
      setLeaveStats({ approved: res.data.approved || 0, pending: res.data.pending || 0 });
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be under 2MB');
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 300, maxHeight: 300, quality: 0.3 });
      await authAPI.updateMyPhoto(compressed);
      setUser(prev => ({ ...prev, profile_photo: compressed }));
      toast.success('Photo updated!');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const tenure = daysSince(user.joining_date);
  const sanctionedLeaves = user.sanctioned_leaves ?? 18;
  const usedLeaves = leaveStats.approved;
  const remainingLeaves = Math.max(0, sanctionedLeaves - usedLeaves);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] pb-28">
      <div className="bg-[#026536] pt-12 pb-8 px-5">
        <div className="max-w-lg mx-auto">
          <h1 data-testid="profile-title" className="text-xl font-extrabold text-white tracking-tight">Profile</h1>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-lg mx-auto space-y-4">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
          <div className="flex items-start gap-4 mb-4">
            <div className="relative group shrink-0">
              {user.profile_photo ? (
                <img src={user.profile_photo} alt={user.name} data-testid="profile-photo" className="h-16 w-16 rounded-full object-cover border-2 border-[#026536]/20" />
              ) : (
                <div data-testid="profile-photo-placeholder" className="h-16 w-16 rounded-full bg-[#026536]/10 flex items-center justify-center">
                  <User className="text-[#026536]" size={30} />
                </div>
              )}
              <label className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <Camera size={16} className="text-white" />
                <input data-testid="employee-photo-upload" type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" ref={fileRef} />
              </label>
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h2 data-testid="profile-name" className="font-bold text-[#1a2529] text-lg leading-tight">{user.name}</h2>
              {(user.designation || user.department) && (
                <p className="text-xs text-[#1a2529]/50 mt-0.5">
                  {user.designation}{user.designation && user.department ? ' · ' : ''}{user.department}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge data-testid="profile-status"
                  className={user.status === 'approved' ? 'bg-[#026536]/10 text-[#026536] border-0 text-xs' : user.status === 'pending' ? 'bg-[#dea439]/10 text-[#dea439] border-0 text-xs' : 'bg-red-100 text-red-600 border-0 text-xs'}
                >{user.status}</Badge>
                {user.employee_number && (
                  <span className="text-[10px] font-semibold text-[#1a2529]/40">#{user.employee_number}</span>
                )}
              </div>
              <button data-testid="change-photo-btn" onClick={() => fileRef.current?.click()} className="text-[11px] text-[#026536] font-semibold mt-1.5 hover:underline">
                Change Photo
              </button>
            </div>
          </div>

          <div className="space-y-0">
            <InfoRow icon={Phone} label="Phone" value={`+91 ${user.phone}`} testId="profile-phone" />
            <InfoRow icon={Shield} label="Role" value={user.role} capitalize />
            {user.employee_number && <InfoRow icon={Hash} label="Employee #" value={user.employee_number} testId="profile-emp-number" />}
            {user.joining_date && (
              <InfoRow icon={Briefcase} label="Joining Date"
                value={formatDate(user.joining_date)}
                testId="profile-joining" />
            )}
            {tenure && <InfoRow icon={Clock} label="With the Organization" value={tenure} testId="profile-tenure" highlight />}
            {user.department && <InfoRow icon={Building2} label="Department" value={user.department} />}
            {user.designation && <InfoRow icon={Crown} label="Designation" value={user.designation} />}
            {user.reporting_manager && <InfoRow icon={Users} label="Reporting Manager" value={user.reporting_manager} />}
            {user.branch && <InfoRow icon={MapPin} label="Branch / HH" value={user.branch} />}
            {user.pf_number && <InfoRow icon={Hash} label="PF Number" value={user.pf_number} testId="profile-pf" />}
            {user.gender && <InfoRow icon={User} label="Gender" value={user.gender} capitalize />}
            {user.dob && (
              <InfoRow icon={Calendar} label="Date of Birth"
                value={formatDate(user.dob)} />
            )}
            {user.current_address && <InfoRow icon={MapPin} label="Address" value={user.current_address} />}
          </div>
        </div>

        {/* Emergency Contact */}
        {(user.emergency_contact_name || user.emergency_contact_number) && (
          <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
            <h3 className="text-sm font-bold text-[#1a2529] mb-3 flex items-center gap-2">
              <AlertCircle size={15} className="text-red-500" /> Emergency Contact
            </h3>
            <div className="space-y-0">
              {user.emergency_contact_name && <InfoRow icon={User} label="Name" value={user.emergency_contact_name} testId="emergency-name" />}
              {user.emergency_contact_number && <InfoRow icon={Phone} label="Number" value={user.emergency_contact_number} testId="emergency-number" />}
            </div>
          </div>
        )}

        {/* Leave Summary */}
        <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
          <h3 className="text-sm font-bold text-[#1a2529] mb-3 flex items-center gap-2">
            <CalendarOff size={15} className="text-[#026536]" /> Leave Summary ({new Date().getFullYear()})
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#f4f4f5] rounded-xl p-3 text-center">
              <p className="text-xl font-extrabold text-[#1a2529]">{sanctionedLeaves}</p>
              <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase mt-0.5">Sanctioned</p>
            </div>
            <div className="bg-[#dea439]/10 rounded-xl p-3 text-center">
              <p className="text-xl font-extrabold text-[#dea439]">{usedLeaves}</p>
              <p className="text-[10px] text-[#dea439]/60 font-semibold uppercase mt-0.5">Used</p>
            </div>
            <div className="bg-[#026536]/10 rounded-xl p-3 text-center">
              <p data-testid="remaining-leaves" className="text-xl font-extrabold text-[#026536]">{remainingLeaves}</p>
              <p className="text-[10px] text-[#026536]/60 font-semibold uppercase mt-0.5">Remaining</p>
            </div>
          </div>
          {leaveStats.pending > 0 && (
            <p className="text-xs text-[#dea439] font-medium mt-2">{leaveStats.pending} leave request(s) pending</p>
          )}
        </div>

        {/* Upcoming Holidays */}
        <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
          <h3 className="text-sm font-bold text-[#1a2529] mb-3 flex items-center gap-2">
            <Gift size={15} className="text-[#dea439]" /> Holidays ({new Date().getFullYear()})
          </h3>
          {holidays.length === 0 ? (
            <p className="text-xs text-[#1a2529]/40 py-2">No holidays listed yet</p>
          ) : (
            <div className="space-y-2">
              {holidays.map((h, idx) => {
                const isPast = new Date(h.date) < new Date(new Date().toDateString());
                return (
                  <div key={h.id} data-testid={`holiday-${idx}`} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg ${isPast ? 'opacity-50' : 'bg-[#f4f4f5]'}`}>
                    <div className="h-10 w-10 rounded-lg bg-[#dea439]/10 flex flex-col items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-[#dea439] leading-none">{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' }).toUpperCase()}</span>
                      <span className="text-sm font-extrabold text-[#1a2529] leading-none mt-0.5">{new Date(h.date).getDate()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a2529] truncate">{h.name}</p>
                      {h.description && <p className="text-[11px] text-[#1a2529]/40 truncate">{h.description}</p>}
                    </div>
                    <span className="text-[10px] text-[#1a2529]/30 font-medium shrink-0">
                      {formatDate(h.date)}
                    </span>
                  </div>
                );
              })}
              <p className="text-center text-[10px] text-[#1a2529]/30 mt-2">{holidays.length} holidays this year</p>
            </div>
          )}
        </div>

        {/* Language Selector */}
        <LanguageSwitcher />

        {/* Push Notifications Toggle */}
        <PushNotificationCard />

        {/* Change Password */}
        <ChangePasswordCard />

        <Button data-testid="logout-btn" onClick={handleLogout} variant="outline"
          className="w-full h-12 rounded-xl border-red-200 text-red-500 hover:bg-red-50 font-semibold">
          <LogOut size={16} className="mr-2" /> Sign Out
        </Button>
      </div>

      <BottomNav />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId, capitalize, highlight }) {
  return (
    <div className="flex items-center gap-3 py-3 border-t border-border/50">
      <Icon size={16} className="text-[#1a2529]/40 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">{label}</p>
        <p data-testid={testId} className={`text-sm font-semibold text-[#1a2529] truncate ${capitalize ? 'capitalize' : ''} ${highlight ? 'text-[#026536]' : ''}`}>{value}</p>
      </div>
    </div>
  );
}

function PushNotificationCard() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setEnabled(!!sub);
      });
    });
  }, []);

  const togglePush = async () => {
    if (!supported) { toast.error('Push notifications are not supported in this browser'); return; }
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (enabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await sub.unsubscribe();
          await pushAPI.unsubscribe(sub.endpoint);
        }
        setEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const vapidRes = await pushAPI.getVapidKey();
        const vapidKey = vapidRes.data?.public_key;
        if (!vapidKey) { toast.error('Push notifications not configured'); setLoading(false); return; }
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') { toast.error('Notification permission denied'); setLoading(false); return; }
        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
        const rawKey = Uint8Array.from(atob(vapidKey.replace(/-/g, '+').replace(/_/g, '/') + padding), c => c.charCodeAt(0));
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
        const subJson = sub.toJSON();
        await pushAPI.subscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
        setEnabled(true);
        toast.success('Push notifications enabled!');
      }
    } catch (err) {
      toast.error('Failed to toggle notifications');
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${enabled ? 'bg-[#026536]/10' : 'bg-[#f4f4f5]'}`}>
            <Bell size={18} className={enabled ? 'text-[#026536]' : 'text-[#1a2529]/40'} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1a2529]">Push Notifications</p>
            <p className="text-[11px] text-[#1a2529]/40">Get alerts for leave & sanction updates</p>
          </div>
        </div>
        <button data-testid="push-toggle-btn" onClick={togglePush} disabled={loading}
          className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#026536]' : 'bg-[#1a2529]/20'}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          {loading && <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </span>}
        </button>
      </div>
    </div>
  );
}

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = i18n.language || 'en';

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    toast.success(`Language changed to ${LANGUAGES.find(l => l.code === code)?.label}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-[#026536]/10 flex items-center justify-center shrink-0">
          <Globe size={18} className="text-[#026536]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1a2529]">Language / भाषा</p>
          <p className="text-[11px] text-[#1a2529]/40">Choose your preferred language</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            data-testid={`lang-${lang.code}`}
            onClick={() => handleChange(lang.code)}
            className={`py-2.5 px-3 rounded-xl text-center transition-all ${
              current === lang.code
                ? 'bg-[#026536] text-white shadow-md'
                : 'bg-[#f4f4f5] text-[#1a2529] hover:bg-[#026536]/10'
            }`}
          >
            <p className="text-sm font-bold">{lang.native}</p>
            <p className={`text-[10px] mt-0.5 ${current === lang.code ? 'text-white/70' : 'text-[#1a2529]/40'}`}>{lang.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}


function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async () => {
    if (!currentPw) { toast.error('Enter your current password'); return; }
    if (newPw.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword(currentPw, newPw);
      toast.success('Password changed successfully!');
      setOpen(false);
      setCurrentPw('');
      setNewPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="open-change-password" onClick={() => setOpen(true)}
        className="w-full bg-white rounded-2xl shadow-sm p-5 text-left animate-slide-up flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#026536]/10 flex items-center justify-center shrink-0">
          <Lock size={18} className="text-[#026536]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1a2529]">Change Password</p>
          <p className="text-[11px] text-[#1a2529]/40">Update your login password</p>
        </div>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
      <h3 className="text-sm font-bold text-[#1a2529] mb-4 flex items-center gap-2">
        <Lock size={15} className="text-[#026536]" /> Change Password
      </h3>
      <div className="space-y-3">
        <div>
          <Label className="text-xs font-semibold text-[#1a2529]/70">Current Password</Label>
          <div className="relative mt-1">
            <Input data-testid="current-password-input" type={showCurrent ? 'text' : 'password'} value={currentPw}
              onChange={e => setCurrentPw(e.target.value)} placeholder="Enter current password" className="h-11 pr-10" />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30">
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-[#1a2529]/70">New Password</Label>
          <div className="relative mt-1">
            <Input data-testid="new-password-input" type={showNew ? 'text' : 'password'} value={newPw}
              onChange={e => setNewPw(e.target.value)} placeholder="At least 4 characters" className="h-11 pr-10"
              onKeyDown={e => e.key === 'Enter' && handleChange()} />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30">
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="save-password-btn" onClick={handleChange} disabled={saving}
          className="flex-1 h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
          {saving ? <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : null}
          Save Password
        </Button>
        <Button variant="outline" onClick={() => { setOpen(false); setCurrentPw(''); setNewPw(''); }}
          className="h-10 font-semibold">
          Cancel
        </Button>
      </div>
    </div>
  );
}
